import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ComparisonPage.css';

function ComparisonPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [minWatt, setMinWatt] = useState(0);
  const [maxWatt, setMaxWatt] = useState(2000);
  const [minWattRange, setMinWattRange] = useState(0);
  const [maxWattRange, setMaxWattRange] = useState(2000);
  const [sortByTentSize, setSortByTentSize] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [selectedModel, setSelectedModel] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      // OPTIMIERT: 1 Call statt N+1 — with-prices/all liefert bestPrice direkt mit
      const res = await fetch('/api/products/with-prices/all?onlineOnly=true');
      if (!res.ok) throw new Error('API Fehler');
      
      const data = await res.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Ungültiges Datenformat');
      }
      
      // Filtere nur Beleuchtung
      const lightingProducts = data.filter(p => p.productTypeName === 'Beleuchtung');
      
      // Ergänze Felder die ComparisonPage erwartet  
      const productsWithFields = lightingProducts.map(p => ({
        ...p,
        manufacturerId: p.manufacturerId ? { _id: p.manufacturerId, name: p.manufacturerName } : null,
      }));
      
      // Berechne Min/Max Watt
      const watts = productsWithFields.map(p => p.watts || 0).filter(w => w > 0);
      const minWattValue = watts.length > 0 ? Math.min(...watts) : 0;
      const maxWattValue = watts.length > 0 ? Math.max(...watts) : 2000;
      
      setProducts(productsWithFields);
      setMinWattRange(minWattValue);
      setMaxWattRange(maxWattValue);
      setMinWatt(minWattValue);
      setMaxWatt(maxWattValue);
      setError('');
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Produkte: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else if (selectedProducts.length < 4) {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const getFilteredProducts = () => {
    let filtered = products;
    
    // Suche nach Name/Hersteller
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(term) ||
        (typeof p.manufacturerId === 'object' ? p.manufacturerId.name : p.manufacturerId || '').toLowerCase().includes(term)
      );
    }
    
    // Filter nach Hersteller
    if (selectedManufacturer) {
      filtered = filtered.filter(p => {
        const pMfg = typeof p.manufacturerId === 'object' ? p.manufacturerId.name : p.manufacturerId;
        return pMfg === selectedManufacturer;
      });
    }
    
    // Filter nach Modell
    if (selectedModel) {
      filtered = filtered.filter(p => p.name === selectedModel);
    }
    
    // Filter nach Watt-Bereich
    filtered = filtered.filter(p => {
      const watts = p.watts || 0;
      return watts >= minWatt && watts <= maxWatt;
    });
    
    // Sortiere nach Zeltgröße wenn ausgewählt
    if (sortByTentSize) {
      filtered = filtered.filter(p => p.growTentSize === sortByTentSize);
    }
    
    return filtered;
  };
  
  // Sammle alle einzigartigen Zeltgrößen
  const getTentSizes = () => {
    const sizes = [...new Set(products.map(p => p.growTentSize).filter(Boolean))];
    return sizes.sort();
  };
  
  // Sammle alle einzigartigen Hersteller
  const getManufacturers = () => {
    const manufacturers = [...new Set(products.map(p => {
      if (typeof p.manufacturerId === 'object') {
        return p.manufacturerId.name;
      }
      return p.manufacturerId;
    }).filter(Boolean))];
    return manufacturers.sort();
  };
  
  // Sammle alle Modelle des ausgewählten Herstellers
  const getModelsByManufacturer = (mfgName) => {
    if (!mfgName) return [];
    const models = products
      .filter(p => {
        const pMfg = typeof p.manufacturerId === 'object' ? p.manufacturerId.name : p.manufacturerId;
        return pMfg === mfgName;
      })
      .map(p => p.name)
      .sort();
    return [...new Set(models)];
  };

  const getSelectedProductObjects = () => {
    return selectedProducts
      .map(id => products.find(p => p._id === id))
      .filter(Boolean);
  };

  if (loading) {
    return (
      <div className="comparison-page">
        <div className="hero">
          <h1>⚖️ Lampen Vergleich</h1>
          <p>Vergleiche bis zu 4 Lampen miteinander</p>
        </div>
        <div className="container">
          <div className="loading">⏳ Lädt Produkte...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="comparison-page">
        <div className="hero">
          <h1>⚖️ Lampen Vergleich</h1>
          <p>Vergleiche bis zu 4 Lampen miteinander</p>
        </div>
        <div className="container">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  const filteredProducts = getFilteredProducts();
  const selectedObjects = getSelectedProductObjects();

  return (
    <div className="comparison-page">
      <div className="hero">
        <h1>⚖️ Lampen Vergleich</h1>
        <p>Vergleiche bis zu 4 Lampen miteinander</p>
      </div>

      <div className="container">
        {/* Auswahlbereich */}
        <div className="selection-section">
          <div className="selection-header">
            <h2>💡 Lampen auswählen ({selectedProducts.length}/4)</h2>
            <div className="filter-group">
              <input
                type="text"
                placeholder="🔍 Nach Name oder Hersteller suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              
              <div className="filter-row-dropdown">
                <div className="filter-item">
                  <label>🏭 Hersteller</label>
                  <select
                    value={selectedManufacturer}
                    onChange={(e) => {
                      setSelectedManufacturer(e.target.value);
                      setSelectedModel(''); // Reset model wenn Hersteller wechselt
                    }}
                    className="filter-select"
                  >
                    <option value="">Alle Hersteller</option>
                    {getManufacturers().map(mfg => (
                      <option key={mfg} value={mfg}>{mfg}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-item">
                  <label>📦 Modell</label>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="filter-select"
                    disabled={!selectedManufacturer}
                  >
                    <option value="">Alle Modelle</option>
                    {getModelsByManufacturer(selectedManufacturer).map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="filter-row">
                <div className="filter-item">
                  <label>⚡ WATT: VON - BIS</label>
                  <div className="watt-inputs">
                    <input
                      type="number"
                      min={minWattRange}
                      max={maxWatt}
                      value={minWatt}
                      onChange={(e) => {
                        const newMin = Number(e.target.value);
                        if (newMin <= maxWatt) setMinWatt(newMin);
                      }}
                      className="watt-input"
                      placeholder="Von"
                    />
                    <span className="watt-separator">—</span>
                    <input
                      type="number"
                      min={minWatt}
                      max={maxWattRange}
                      value={maxWatt}
                      onChange={(e) => {
                        const newMax = Number(e.target.value);
                        if (newMax >= minWatt) setMaxWatt(newMax);
                      }}
                      className="watt-input"
                      placeholder="Bis"
                    />
                  </div>
                  <div className="slider-container">
                    {/* Weißer Track zwischen min und max */}
                    <div
                      className="slider-track"
                      style={{
                        left: `${((minWatt - minWattRange) / (maxWattRange - minWattRange)) * 100}%`,
                        right: `${100 - ((maxWatt - minWattRange) / (maxWattRange - minWattRange)) * 100}%`,
                      }}
                    />
                    
                    <input
                      type="range"
                      min={minWattRange}
                      max={maxWattRange}
                      value={minWatt}
                      onChange={(e) => {
                        const newMin = Number(e.target.value);
                        if (newMin <= maxWatt) setMinWatt(newMin);
                      }}
                      className="slider slider-min"
                    />
                    <input
                      type="range"
                      min={minWattRange}
                      max={maxWattRange}
                      value={maxWatt}
                      onChange={(e) => {
                        const newMax = Number(e.target.value);
                        if (newMax >= minWatt) setMaxWatt(newMax);
                      }}
                      className="slider slider-max"
                    />
                  </div>
                </div>
                
                <div className="filter-item">
                  <label>📏 Zeltgröße</label>
                  <select
                    value={sortByTentSize}
                    onChange={(e) => setSortByTentSize(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Alle Größen</option>
                    {getTentSizes().map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="product-selector">
            {filteredProducts.length === 0 ? (
              <div className="no-results">Keine Produkte gefunden</div>
            ) : (
              filteredProducts.map(product => (
                <div
                  key={product._id}
                  className={`selector-card ${selectedProducts.includes(product._id) ? 'selected' : ''}`}
                  onClick={() => toggleProduct(product._id)}
                >
                  <div className="selector-card-image">
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : (
                      <div className="image-placeholder">💡</div>
                    )}
                  </div>
                  <div className="selector-card-info">
                    <h4>{product.name}</h4>
                    <p>
                      {typeof product.manufacturerId === 'object' 
                        ? product.manufacturerId.name 
                        : 'Unbekannt'}
                    </p>
                    {product.watts && <span className="badge">{product.watts}W</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Vergleichsbereich */}
        {selectedObjects.length > 0 && (
          <div className="comparison-section">
            <h2>📊 Vergleich</h2>
            <div className="comparison-cards">
              {selectedObjects.map(product => (
                <div key={product._id} className={`quartett-card ${!product.dimmerIncluded ? 'missing-dimmer-warning' : ''}`}>
                  {/* Kartenkopf */}
                  <div className="card-header">
                    <div className="header-top">
                      <div className="header-title">
                        <h3>{product.name}</h3>
                        <p className="manufacturer">
                          {typeof product.manufacturerId === 'object' 
                            ? product.manufacturerId.name 
                            : 'Unbekannt'}
                        </p>
                      </div>
                      {product.bestPrice && (
                        <div 
                          className="header-price"
                          onClick={() => navigate(`/product/${product._id}/suche`)}
                          style={{ cursor: 'pointer' }}
                          title="Klick hier um die Preissuche zu sehen"
                        >
                          <span className="price-label">💰</span>
                          <span className="price-value">€{product.bestPrice.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bild */}
                  <div className="card-image">
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : (
                      <div className="image-placeholder">💡</div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="card-stats">
                    <div className="stat-row">
                      <div className="stat">
                        <span className="stat-label">⚡ Watt</span>
                        <span className="stat-value">{product.watts || '—'} W</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">☀️ µmol/s</span>
                        <span className="stat-value">{product.umolPerSecond || '—'}</span>
                      </div>
                    </div>

                    <div className="stat-row">
                      <div className="stat">
                        <span className="stat-label">💀 Lebensdauer</span>
                        <span className="stat-value">{product.lifespan || '—'} h</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">⭐ Effizienz</span>
                        <span className="stat-value">{product.efficiency || '—'} µmol/J</span>
                      </div>
                    </div>

                    <div className="stat-row">
                      <div className="stat">
                        <span className="stat-label">📏 Fläche</span>
                        <span className="stat-value">{product.growTentSize || '—'}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">⚡💰 Watt/€</span>
                        <span className="stat-value">{product.bestPrice ? (product.watts / product.bestPrice).toFixed(2) : '—'}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="stat-row features-row">
                      {product.smartControl && product.smartControl !== '' && (
                        <span className="feature-badge smart-control">🎛️ {product.smartControl}</span>
                      )}
                      {product.dimmable && product.dimmable !== 'nicht dimmbar' && (
                        <span className="feature-badge dimmable">🔦 {product.dimmable}</span>
                      )}
                      {!product.dimmerIncluded && (
                        <span className="feature-badge missing-dimmer">⚠️ Kein Dimmer</span>
                      )}
                    </div>

                    {product.datasheet && (
                      <div className="stat-row full">
                        <a href={product.datasheet} target="_blank" rel="noopener noreferrer" className="btn-datasheet">
                          📄 Datenblatt
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Remove Button */}
                  <button 
                    className="btn-remove"
                    onClick={() => toggleProduct(product._id)}
                  >
                    ✕ Entfernen
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedObjects.length === 0 && (
          <div className="empty-state">
            <h2>Wähle Lampen zum Vergleichen aus</h2>
            <p>Du kannst bis zu 4 Lampen gleichzeitig vergleichen</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ComparisonPage;
